// Pure, backend-free view logic for the GitHub-Projects-style project board: presentation metadata
// (status/priority/type), client-side filter / group / sort, sub-issue rollup, and saved-view
// persistence. Kept out of the React view so it is unit-testable and reused across board/table/roadmap.

import type { Issue, IssuePriority, IssueStatus, IssueType } from "../models/project";
import { WORK_AREAS, workAreaLabel } from "../models/eqms";

// ---- presentation metadata ----

export const STATUS_ORDER: IssueStatus[] = ["BACKLOG", "TODO", "IN_PROGRESS", "IN_REVIEW", "DONE"];
export const STATUS_META: Record<IssueStatus, { label: string; cls: string; desc: string }> = {
  BACKLOG: { label: "백로그", cls: "st-backlog", desc: "아직 시작하지 않음" },
  TODO: { label: "할 일", cls: "st-todo", desc: "착수 준비됨" },
  IN_PROGRESS: { label: "진행 중", cls: "st-in-progress", desc: "작업 중" },
  IN_REVIEW: { label: "검토 중", cls: "st-in-review", desc: "리뷰 중" },
  DONE: { label: "완료", cls: "st-done", desc: "완료됨" },
};

export const PRIORITY_ORDER: IssuePriority[] = ["HIGHEST", "HIGH", "MEDIUM", "LOW", "LOWEST"];
export const PRIORITY_META: Record<IssuePriority, { label: string; glyph: string; cls: string }> = {
  HIGHEST: { label: "최상", glyph: "⏫", cls: "highest" },
  HIGH: { label: "높음", glyph: "🔼", cls: "high" },
  MEDIUM: { label: "보통", glyph: "▸", cls: "medium" },
  LOW: { label: "낮음", glyph: "🔽", cls: "low" },
  LOWEST: { label: "최하", glyph: "⏬", cls: "lowest" },
};

export const TYPE_LABEL: Record<IssueType, string> = {
  EPIC: "에픽", STORY: "스토리", TASK: "작업", BUG: "버그", SUBTASK: "하위 작업",
  REQUIREMENT: "요구사항", DESIGN: "디자인", TEST_CLASS: "테스트 분류", TEST_ITEM: "테스트 항목",
};
/** Regulatory traceability types (요구-설계-테스트) — visually emphasized on cards. */
export const TRACE_TYPES: ReadonlySet<IssueType> = new Set(["REQUIREMENT", "DESIGN", "TEST_CLASS", "TEST_ITEM"]);

// ---- sub-issue rollup (client-side, from the full unfiltered issue list) ----

export interface SubtaskRollup { done: number; total: number; pct: number }

/** One O(n) pass: parentId → {done,total,pct}. Pass the widest issue list available so a client-side
 * scoped/filtered board still counts children that fell outside the on-screen filter. Note: if the
 * source list is itself server-scoped (e.g. useIssues(projectId, workArea)), children outside that
 * scope are absent and the rollup undercounts — acceptable for the work-area lens. */
export function rollupSubtasks(all: Issue[]): Map<string, SubtaskRollup> {
  const acc = new Map<string, { done: number; total: number }>();
  for (const i of all) {
    if (!i.parentId) continue;
    const cur = acc.get(i.parentId) ?? { done: 0, total: 0 };
    cur.total += 1;
    if (i.status === "DONE") cur.done += 1;
    acc.set(i.parentId, cur);
  }
  const out = new Map<string, SubtaskRollup>();
  for (const [id, { done, total }] of acc) {
    out.set(id, { done, total, pct: total === 0 ? 0 : Math.round((done / total) * 100) });
  }
  return out;
}

// ---- filtering ----

export interface FilterState {
  keyword: string;
  statuses: IssueStatus[];
  priorities: IssuePriority[];
  assignees: string[];   // user ids; the sentinel "__none__" matches unassigned
  types: IssueType[];
  workAreas: string[];
  labels: string[];
}

export const UNASSIGNED = "__none__";

export function emptyFilter(): FilterState {
  return { keyword: "", statuses: [], priorities: [], assignees: [], types: [], workAreas: [], labels: [] };
}

export function activeFilterCount(f: FilterState): number {
  return (f.keyword.trim() ? 1 : 0) + f.statuses.length + f.priorities.length + f.assignees.length
    + f.types.length + f.workAreas.length + f.labels.length;
}

/** AND across fields, OR within a field. */
export function applyFilters(issues: Issue[], f: FilterState): Issue[] {
  const kw = f.keyword.trim().toLowerCase();
  return issues.filter((i) => {
    if (kw && !(`${i.key} ${i.summary}`.toLowerCase().includes(kw))) return false;
    if (f.statuses.length && !f.statuses.includes(i.status)) return false;
    if (f.priorities.length && !f.priorities.includes(i.priority)) return false;
    if (f.types.length && !f.types.includes(i.type)) return false;
    if (f.assignees.length) {
      const key = i.assigneeId ?? UNASSIGNED;
      if (!f.assignees.includes(key)) return false;
    }
    if (f.workAreas.length && !i.workAreaTypes.some((w) => f.workAreas.includes(w))) return false;
    if (f.labels.length && !i.labels.some((l) => f.labels.includes(l))) return false;
    return true;
  });
}

export function distinctLabels(issues: Issue[]): string[] {
  const s = new Set<string>();
  issues.forEach((i) => i.labels.forEach((l) => s.add(l)));
  return [...s].sort();
}

// ---- grouping ----

export type GroupAxis = "status" | "priority" | "assignee" | "workArea";

export interface BoardColumn {
  key: string;
  label: string;
  cls?: string;       // status/priority dot class
  desc?: string;      // column subtitle (status axis only)
  presetStatus?: IssueStatus;
  presetPriority?: IssuePriority;
  items: Issue[];
}

/** Splits issues into ordered columns for the given axis. `labelFor` resolves an assignee id to a name. */
export function groupIssues(
  issues: Issue[],
  axis: GroupAxis,
  memberIds: string[],
  labelFor: (id: string) => string,
): BoardColumn[] {
  if (axis === "status") {
    return STATUS_ORDER.map((s) => ({
      key: s, label: STATUS_META[s].label, cls: STATUS_META[s].cls, desc: STATUS_META[s].desc,
      presetStatus: s, items: issues.filter((i) => i.status === s),
    }));
  }
  if (axis === "priority") {
    return PRIORITY_ORDER.map((p) => ({
      key: p, label: PRIORITY_META[p].label, cls: `pri-dot-${PRIORITY_META[p].cls}`,
      presetPriority: p, items: issues.filter((i) => i.priority === p),
    }));
  }
  if (axis === "workArea") {
    const cols: BoardColumn[] = WORK_AREAS.map((a) => ({
      key: a, label: workAreaLabel(a), items: issues.filter((i) => i.workAreaTypes.includes(a)),
    }));
    cols.push({ key: "__none__", label: "미지정", items: issues.filter((i) => i.workAreaTypes.length === 0) });
    return cols;
  }
  // assignee
  const ids = [...memberIds];
  const cols: BoardColumn[] = ids.map((id) => ({
    key: id, label: labelFor(id), items: issues.filter((i) => i.assigneeId === id),
  }));
  cols.push({ key: UNASSIGNED, label: "미배정", items: issues.filter((i) => !i.assigneeId) });
  // drop empty member columns to avoid a wall of empty lanes, but always keep 미배정
  return cols.filter((c) => c.items.length > 0 || c.key === UNASSIGNED);
}

// ---- sorting (table view) ----

export type SortKey = "number" | "summary" | "status" | "priority" | "assignee" | "points" | "dueDate";
export type SortDir = "asc" | "desc";

export function sortIssues(issues: Issue[], key: SortKey, dir: SortDir, labelFor: (id: string) => string): Issue[] {
  const sign = dir === "asc" ? 1 : -1;
  const val = (i: Issue): number | string => {
    switch (key) {
      case "number": return i.number;
      case "summary": return i.summary.toLowerCase();
      case "status": return STATUS_ORDER.indexOf(i.status);
      case "priority": return PRIORITY_ORDER.indexOf(i.priority);
      case "assignee": return i.assigneeId ? labelFor(i.assigneeId).toLowerCase() : "￿";
      case "points": return i.storyPoints ?? -1;
      case "dueDate": return i.dueDate ?? "￿";
    }
  };
  return [...issues].sort((a, b) => {
    const va = val(a), vb = val(b);
    if (va < vb) return -1 * sign;
    if (va > vb) return 1 * sign;
    return 0;
  });
}

// ---- saved views (client-side / localStorage) ----

export type ViewType = "board" | "table" | "roadmap";

export interface SavedView {
  id: string;
  name: string;
  view: ViewType;
  groupBy: GroupAxis;
  filter: FilterState;
  sortKey?: SortKey;
  sortDir?: SortDir;
  builtin?: boolean;
}

/** Built-in preset tabs, mirroring GitHub Projects' saved views. `meId` scopes the "내 이슈" preset. */
export function defaultViews(meId: string | null): SavedView[] {
  return [
    { id: "board", name: "보드", view: "board", groupBy: "status", filter: emptyFilter(), builtin: true },
    { id: "priority", name: "우선순위", view: "board", groupBy: "priority", filter: emptyFilter(), builtin: true },
    { id: "table", name: "표", view: "table", groupBy: "status", filter: emptyFilter(), sortKey: "priority", sortDir: "desc", builtin: true },
    { id: "roadmap", name: "로드맵", view: "roadmap", groupBy: "status", filter: emptyFilter(), builtin: true },
    {
      id: "mine", name: "내 이슈", view: "board", groupBy: "status", builtin: true,
      filter: { ...emptyFilter(), assignees: meId ? [meId] : [] },
    },
  ];
}

const SCHEMA = "v1";

/** Loads persisted custom views (built-ins are always regenerated fresh, never persisted). */
export function loadCustomViews(storageKey: string): SavedView[] {
  try {
    const raw = localStorage.getItem(`pbv:${SCHEMA}:${storageKey}`);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as SavedView[]).filter((v) => v && !v.builtin) : [];
  } catch {
    return [];
  }
}

export function saveCustomViews(storageKey: string, views: SavedView[]): void {
  try {
    localStorage.setItem(`pbv:${SCHEMA}:${storageKey}`, JSON.stringify(views.filter((v) => !v.builtin)));
  } catch {
    // ignore quota / unavailable storage — saved views are a convenience, not correctness
  }
}
